import * as React from "react"
import { ChevronDown, Info } from "lucide-react"

import { HELP_TEXT, META_TEXT, TAP_TARGET } from "@/lib/ui-tokens"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu"
import { Input } from "@/shadcn/components/ui/input"
import { Separator } from "@/shadcn/components/ui/separator"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/shadcn/components/ui/toggle-group"
import { SourceChip } from "./marks"
import { optionPairs } from "./options"
import { OutputAvatar, type OutputAccount } from "./OutputTile"
import { type ConfigureOption, type ConnectedSource } from "./types"

/**
 * Step 3 · review and create.
 *
 * The two choice fields map onto real input fields in prod — `firstPullFlag`
 * and `postFlag`. Their options and helper copy come from
 * `@/lib/review-behavior`, which owns the constant-to-label mapping;
 * InputBehaviorComponent.jsx remains the source of truth for which constant
 * means what.
 */

/**
 * Single-select field: a dropdown below `sm`, the segmented ToggleGroup from
 * `sm` up.
 *
 * Two subtrees rather than one responsive control, because `spacing` and
 * `orientation` on ToggleGroup are props driving `data-` attributes, not classes
 * — so the joined-segment layout can't become a gapped or vertical one at a
 * breakpoint. Joined, three options need ~378px against the 271px a mobile card
 * offers, and the wrap put a detached fully-rounded pill on a second row,
 * because `spacing={0}` rounds only the first and last item.
 *
 * Two controls for one value: `onChange` drives both, so they can't disagree.
 * Omit it to render them read-only, as the design harness does.
 *
 * `value` is the STORED value, which is only the same thing as the label when
 * the options are bare strings. Prod's two fields hold numeric constants, so
 * both controls select by value and display the matching option's label.
 */
function ChoiceField({
  label,
  options,
  value,
  help,
  onChange,
}: {
  label: string
  options: ConfigureOption[]
  value: string
  help?: string
  onChange?: (value: string) => void
}) {
  const pairs = optionPairs(options)
  // Falls back to the raw value rather than to the first option: a value that
  // matches nothing is a caller bug, and showing option one would hide it while
  // implying the user had chosen it.
  const current = pairs.find((option) => option.value === value)?.label ?? value

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <p className="tw:text-sm tw:font-medium tw:text-foreground">{label}</p>

      {/* modal={false}: a modal Radix menu locks the page's scroll while it
          is down, which on the one control that only exists at mobile is the
          worst place for it. */}
      <DropdownMenu modal={false}>
        {/* h-11 rather than TAP_TARGET: this trigger only exists below `sm`, so a
            `md:` restoration would be dead code. Content width comes from the
            primitive's --radix-dropdown-menu-trigger-width, matching the
            full-width trigger. */}
        <DropdownMenuTrigger
          className="tw:inline-flex tw:h-11 tw:w-full tw:items-center tw:justify-between tw:gap-2 tw:rounded-lg tw:border tw:border-input tw:px-3 tw:text-sm tw:text-foreground tw:transition-colors tw:outline-none tw:hover:bg-muted tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:data-[state=open]:bg-muted tw:sm:hidden"
          aria-label={`${label} ${current}`}
        >
          {current}
          <ChevronDown className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
        </DropdownMenuTrigger>
        {/* Portal patch: portaled content sits outside `.tw-root`. */}
        <DropdownMenuContent className="tw-root">
          <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
            {pairs.map((option) => (
              <DropdownMenuRadioItem key={option.value} value={option.value}>
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <ToggleGroup
        type="single"
        spacing={0}
        variant="outline"
        value={value}
        onValueChange={
          onChange ? (next) => next && onChange(next) : undefined
        }
        className="tw:hidden tw:w-fit tw:max-w-full tw:flex-wrap tw:sm:flex"
      >
        {pairs.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            className={TAP_TARGET}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {help ? <p className={HELP_TEXT}>{help}</p> : null}
    </div>
  )
}

export function ReviewStep({
  base,
  source,
  accounts,
  automationName,
  postFirstOptions,
  postFirst,
  postFirstHelp,
  postWhenOptions,
  postWhen,
  postWhenHelp,
  reassurance,
  onNameChange,
  onPostFirstChange,
  onPostWhenChange,
}: {
  base: string
  source: ConnectedSource
  /** The whole destination list; only the selected ones are drawn. */
  accounts: OutputAccount[]
  automationName: string
  postFirstOptions: ConfigureOption[]
  /** The stored value, not the label — see ChoiceField. */
  postFirst: string
  postFirstHelp?: string
  postWhenOptions: ConfigureOption[]
  postWhen: string
  postWhenHelp?: string
  reassurance?: string
  onNameChange?: (name: string) => void
  onPostFirstChange?: (value: string) => void
  onPostWhenChange?: (value: string) => void
}) {
  const selected = accounts.filter((a) => a.selected)
  const shown = selected.slice(0, 4)
  const overflow = selected.length - shown.length
  const networkCount = new Set(selected.map((a) => a.network)).size

  return (
    /* p-4 at mobile: 24px on all sides of a 303px card spent 48px of the width
       on padding alone (8% of the 600px design width, 16% here). */
    <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-[600px] tw:flex-col tw:gap-6 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-4 tw:md:p-6">
      <div className="tw:flex tw:flex-col tw:gap-4">
        <p className="tw:text-base tw:font-semibold tw:text-foreground">
          Your automation
        </p>

        <SourceChip source={source} base={base} size="md" />

        {/* Stacks below `sm`. As a wrapping row it needed 220px of 255px, so it
            broke around 340px into a label on one line and avatars on the next —
            the same two-line result, but accidental, and pt-1.5 (there to
            optically centre the label against the avatar row) became stray
            padding on an orphaned label. */}
        <div className="tw:flex tw:flex-col tw:gap-2 tw:sm:flex-row tw:sm:flex-wrap tw:sm:items-start tw:sm:gap-4">
          <span className="tw:text-sm tw:text-muted-foreground tw:sm:pt-1.5">
            Destinations
          </span>
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            {/* gap-2 rather than the 1.5 this row used at 26px: the badge
                overhangs its avatar by 3px, so the clearance between one badge
                and the next avatar is the gap minus that. At 18px badges the
                old 6px left them visibly touching. */}
            <div className="tw:flex tw:items-center tw:gap-2">
              {/* OutputAvatar rather than a monogram of our own: it is the same
                  avatar the destination tiles draw, so this stack shows the real
                  profile picture, falls back to the initials when there isn't one
                  (or it 404s), and inherits the badge and inactive treatment
                  instead of re-deriving them and drifting.

                  badgeSize is pinned rather than left to the default, which
                  would compute size * 0.46 and shrink the marks the Figma frame
                  draws. */}
              {shown.map((account) => (
                <OutputAvatar
                  key={account.id}
                  network={account.network}
                  initials={account.initials}
                  avatarUrl={account.avatarUrl}
                  badgeIcon={account.badgeIcon}
                  badgeLabel={account.badgeLabel}
                  inactive={account.inactive}
                  disconnected={account.disconnected}
                  size={32}
                  badgeSize={18}
                  textClassName="tw:text-xs"
                />
              ))}
              {overflow > 0 ? (
                <span className="tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:bg-muted tw:text-xs tw:text-muted-foreground">
                  +{overflow}
                </span>
              ) : null}
            </div>
            <p role="status" className={META_TEXT}>
              {selected.length} account{selected.length === 1 ? '' : 's'} across{' '}
              {networkCount} network{networkCount === 1 ? '' : 's'}
            </p>
          </div>
        </div>
      </div>

      <Separator />

      <div className="tw:flex tw:flex-col tw:gap-2">
        <p className="tw:text-sm tw:font-medium tw:text-foreground">
          Automation name
        </p>
        <Input
          readOnly={!onNameChange}
          value={onNameChange ? automationName : undefined}
          defaultValue={onNameChange ? undefined : automationName}
          onChange={
            onNameChange
              ? (event) => onNameChange(event.target.value)
              : undefined
          }
          className={TAP_TARGET}
        />
      </div>

      <ChoiceField
        label="What should we post first?"
        options={postFirstOptions}
        value={postFirst}
        help={postFirstHelp}
        onChange={onPostFirstChange}
      />

      <ChoiceField
        label="When should we post new items?"
        options={postWhenOptions}
        value={postWhen}
        help={postWhenHelp}
        onChange={onPostWhenChange}
      />

      {reassurance ? (
        <p className={cn("tw:flex tw:items-center tw:gap-1.5", META_TEXT)}>
          <Info className="tw:size-3.5 tw:shrink-0" />
          {reassurance}
        </p>
      ) : null}
    </div>
  )
}
